(function () {
  function findVariant(variants, selectedOptions) {
    return variants.find(
      (variant) =>
        variant.options.length === selectedOptions.length &&
        variant.options.every((value, index) => value === selectedOptions[index])
    );
  }

  class MJCProductVariants extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-variant-form]');
      this.variantIdInput = this.querySelector('[data-variant-id]');
      this.price = this.querySelector('[data-variant-price]');
      this.submitButton = this.querySelector('[data-variant-submit]');
      this.submitLabel = this.querySelector('[data-variant-submit-label]');
      this.mobileSubmitButton = this.querySelector('[data-mobile-variant-submit]');
      this.mobileSubmitLabel = this.querySelector('[data-mobile-variant-submit-label]');
      this.mobilePrice = this.querySelector('[data-mobile-variant-price]');
      this.mainImage = this.querySelector('.mjc-gallery__main-img');
      this.galleryCounter = this.querySelector('[data-gallery-counter]');
      this.galleryDialog = this.querySelector('[data-gallery-dialog]');
      this.galleryDialogImage = this.querySelector('[data-gallery-dialog-image]');
      this.galleryExpand = this.querySelector('[data-gallery-expand]');
      this.galleryClose = this.querySelector('[data-gallery-close]');
      this.selects = Array.from(this.querySelectorAll('.mjc-product__select'));
      this.thumbnails = Array.from(this.querySelectorAll('.mjc-gallery__thumb'));

      const variantData = this.querySelector('[data-mjc-product-variants]');
      if (!this.form || !this.variantIdInput || !variantData) return;

      try {
        this.variants = JSON.parse(variantData.textContent);
      } catch (error) {
        console.error('Unable to read product variants', error);
        this.disableForm(this.dataset.unavailable);
        return;
      }

      this.selects.forEach((select) => select.addEventListener('change', () => this.onOptionsChange()));
      this.thumbnails.forEach((thumbnail) =>
        thumbnail.addEventListener('click', () => this.showThumbnail(thumbnail))
      );
      if (this.galleryExpand && this.galleryDialog) {
        this.galleryExpand.addEventListener('click', () => this.openGalleryDialog());
      }
      if (this.galleryClose && this.galleryDialog) {
        this.galleryClose.addEventListener('click', () => this.galleryDialog.close());
      }
      if (this.galleryDialog) {
        this.galleryDialog.addEventListener('click', (event) => {
          if (event.target === this.galleryDialog) this.galleryDialog.close();
        });
      }
      if (this.mobileSubmitButton) {
        this.mobileSubmitButton.addEventListener('click', () => {
          if (!this.submitButton.disabled) this.submitButton.click();
        });
      }

      const initialVariant = this.variants.find(
        (variant) => String(variant.id) === String(this.dataset.selectedVariantId)
      );
      if (initialVariant) this.selectOptions(initialVariant.options);
      this.applyVariant(initialVariant || findVariant(this.variants, this.selectedOptions()), false);
    }

    selectedOptions() {
      return this.selects.map((select) => select.value);
    }

    selectOptions(options) {
      this.selects.forEach((select, index) => {
        if (options[index] !== undefined) select.value = options[index];
      });
    }

    onOptionsChange() {
      this.applyVariant(findVariant(this.variants, this.selectedOptions()), true);
    }

    applyVariant(variant, updateUrl) {
      if (!variant) {
        this.variantIdInput.value = '';
        this.dataset.selectedVariantId = '';
        this.disableForm(this.dataset.unavailable);
        return;
      }

      this.variantIdInput.value = String(variant.id);
      this.dataset.selectedVariantId = String(variant.id);
      if (this.price) this.price.textContent = variant.price;
      if (this.mobilePrice) this.mobilePrice.textContent = variant.price;

      if (variant.available) {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-disabled');
        if (this.submitLabel) this.submitLabel.textContent = this.dataset.addToCart;
        if (this.mobileSubmitButton) this.mobileSubmitButton.disabled = false;
        if (this.mobileSubmitLabel) this.mobileSubmitLabel.textContent = this.dataset.addToCart;
      } else {
        this.disableForm(this.dataset.soldOut);
      }

      if (variant.featuredImage) this.showVariantImage(variant.featuredImage);
      if (updateUrl) this.updateUrl(variant.id);

      this.dispatchEvent(
        new CustomEvent('mjc:variant-change', {
          bubbles: true,
          detail: { variant },
        })
      );
    }

    disableForm(label) {
      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
      }
      if (this.submitLabel) this.submitLabel.textContent = label;
      if (this.mobileSubmitButton) this.mobileSubmitButton.disabled = true;
      if (this.mobileSubmitLabel) this.mobileSubmitLabel.textContent = label;
    }

    updateUrl(variantId) {
      if (!window.history || !this.dataset.productUrl) return;
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    }

    showVariantImage(image) {
      if (!this.mainImage) return;
      this.mainImage.src = image.src;
      this.mainImage.srcset = image.srcset;
      this.mainImage.alt = image.alt || '';
      if (image.width) this.mainImage.width = image.width;
      if (image.height) this.mainImage.height = image.height;
      this.setActiveThumbnail(image.id);
      this.syncDialogImage();
    }

    showThumbnail(thumbnail) {
      if (!this.mainImage) return;
      this.mainImage.src = thumbnail.dataset.imageSrc;
      this.mainImage.srcset = thumbnail.dataset.imageSrcset;
      this.mainImage.alt = thumbnail.dataset.imageAlt || '';
      this.setActiveThumbnail(thumbnail.dataset.imageId);
      this.updateGalleryCounter(thumbnail.dataset.imagePosition);
      this.syncDialogImage();
    }

    setActiveThumbnail(imageId) {
      this.thumbnails.forEach((thumbnail) => {
        const active = String(thumbnail.dataset.imageId) === String(imageId);
        thumbnail.classList.toggle('mjc-gallery__thumb--active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) this.updateGalleryCounter(thumbnail.dataset.imagePosition);
      });
    }

    updateGalleryCounter(position) {
      if (!this.galleryCounter || !position) return;
      this.galleryCounter.textContent = `${String(position).padStart(2, '0')} / ${String(this.thumbnails.length).padStart(2, '0')}`;
    }

    syncDialogImage() {
      if (!this.mainImage || !this.galleryDialogImage) return;
      this.galleryDialogImage.src = this.mainImage.currentSrc || this.mainImage.src;
      this.galleryDialogImage.srcset = this.mainImage.srcset;
      this.galleryDialogImage.alt = this.mainImage.alt;
    }

    openGalleryDialog() {
      if (!this.galleryDialog) return;
      this.syncDialogImage();
      if (typeof this.galleryDialog.showModal === 'function') this.galleryDialog.showModal();
    }
  }

  window.MJCProductVariants = { findVariant };
  if (!customElements.get('mjc-product-variants')) {
    customElements.define('mjc-product-variants', MJCProductVariants);
  }
})();
